import { useState, useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface CloneActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newActionName: string) => void;
  originalActionName?: string;
  onCloseAutoFocus?: (event: Event) => void;
}

export function CloneActionDialog({
  isOpen,
  onClose,
  onConfirm,
  originalActionName = "Action",
  onCloseAutoFocus,
}: CloneActionDialogProps) {
  const [newActionName, setNewActionName] = useState(`${originalActionName} (clone)`);

  useEffect(() => {
    if (isOpen) {
      setNewActionName(`${originalActionName} (clone)`);
    }
  }, [originalActionName, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newActionName.trim()) {
      onConfirm(newActionName.trim());
      setNewActionName(`${originalActionName} (clone)`);
      onClose();
    }
  };

  const handleClose = () => {
    setNewActionName(`${originalActionName} (clone)`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle className="text-white">Clone Action</DialogTitle>
          <DialogDescription className="sr-only">
            Enter a name for the cloned action and confirm to create a copy.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="py-4">
            <Label htmlFor="cloneName" className="text-white">
              New Action Name
            </Label>
            <Input
              id="cloneName"
              value={newActionName}
              onChange={(e) => setNewActionName(e.target.value)}
              className="mt-2 bg-surface-input border-surface-border text-white placeholder:text-muted focus:ring-primary"
              placeholder="Enter new action name"
              autoFocus
            />
          </div>
          <DialogFooter>
            {/* Route Cancel through Radix's Close primitive — see
                clone-persona-dialog for the DEF-792-02 rationale. */}
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary-dark text-white"
              disabled={!newActionName.trim()}
            >
              Clone Action
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}